/**
 * Catálogo de planos do checkout de renovação (troca de plano — spec §4).
 *
 * O selo de economia dos anuais é SEMPRE calculado das tabelas de preço: 12x o
 * mensal equivalente menos o custo anual do plano. Sem referência mensal ou com
 * economia não-positiva o selo simplesmente não aparece — valor inventado num
 * argumento de venda é pior do que argumento nenhum.
 */

export interface RenewalPlanOption {
  id: string;
  name: string;
  slug: string | null;
  billingCycle: 'monthly' | 'annual';
  planType: string | null;
  /** Preço de tabela: total do ano nos anuais, valor do mês nos mensais. */
  discountedPrice: number;
  originalPrice: number | null;
  discountPercentage: number;
  /** #128: exige Turnstile no checkout (plans.requires_turnstile). */
  requiresTurnstile: boolean;
}

/**
 * Preço pode chegar como number, como decimal do Postgres ("89.90") ou já
 * formatado ("R$ 89,90") — o mesmo campo tem os três formatos dependendo do
 * endpoint que devolveu.
 */
export function parsePrice(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;

  const limpo = value.replace(/[^\d,.-]/g, '');
  const decimalComVirgula = /,\d{1,2}$/.test(limpo);
  const normalizado = decimalComVirgula
    ? limpo.replace(/\./g, '').replace(',', '.')
    : limpo.replace(/,/g, '');

  const numero = parseFloat(normalizado);
  return Number.isFinite(numero) ? numero : 0;
}

/**
 * Família do plano = o nome sem o sufixo do ciclo. É como o ChangePlanModal
 * pareia mensal e anual (mesmo nome nos dois ciclos, alternados pelo toggle).
 */
const DIACRITICOS = /[\u0300-\u036f]/g;

export function normalizePlanFamily(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/\b(plano|assinatura|mensal|anual|recorrente)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Plano como chega da API (catálogo público ou endpoint de renovação): campos
 * opcionais e preço em qualquer um dos formatos que `parsePrice` aceita.
 */
export interface RawPlanInput {
  id?: string | number | null;
  name?: string | null;
  slug?: string | null;
  billingCycle?: string | null;
  planType?: string | null;
  price?: unknown;
  discountedPrice?: unknown;
  originalPrice?: unknown;
  discountPercentage?: unknown;
  visible?: boolean | null;
  active?: boolean | null;
  /** Venda bloqueada (plans.sale_blocked): não é opção de troca nem de upgrade. */
  saleBlocked?: boolean | null;
  requiresTurnstile?: boolean | null;
}

// Venda bloqueada fica fora do catálogo do seletor (o backend já não a manda
// no GET /plans?visible=true — isto é defesa). O plano ATUAL não passa por
// aqui: entra sempre em buildRenewalPlanOptions, bloqueado ou não.
const isVisibleAndActive = (plan: RawPlanInput | null | undefined): boolean =>
  plan?.visible !== false && plan?.active !== false && plan?.saleBlocked !== true;

const toOption = (
  plan: RawPlanInput | null | undefined,
): RenewalPlanOption | null => {
  if (!plan?.id) return null;
  const original = parsePrice(plan.originalPrice);
  return {
    id: String(plan.id),
    name: String(plan.name ?? ''),
    slug: plan.slug ?? null,
    billingCycle: plan.billingCycle === 'annual' ? 'annual' : 'monthly',
    planType: plan.planType ?? null,
    discountedPrice: parsePrice(plan.discountedPrice ?? plan.price),
    originalPrice: original > 0 ? original : null,
    discountPercentage: Number(plan.discountPercentage) || 0,
    requiresTurnstile: plan.requiresTurnstile === true,
  };
};

/**
 * Nível do produto: Descoberta (1) < Explorador (2) < Aventureiro (3), pelo
 * nome (retenção/parcelado/desconto são o mesmo produto com outro contrato).
 * Espelho de `planTier` do backend (renewal-eligibility.ts).
 */
export function planTier(name: string | null | undefined): number | null {
  const familia = normalizePlanFamily(name ?? '');
  if (/\baventureiro\b/.test(familia)) return 3;
  if (/\bexplorador\b/.test(familia)) return 2;
  if (/\bdescoberta\b/.test(familia)) return 1;
  return null;
}

/**
 * Compromisso: mensal puro (0) < anual pago por mês (1) < anual (2).
 * Espelho de `planCommitment` do backend.
 */
export function planCommitment(plan: Pick<RenewalPlanOption, 'billingCycle' | 'planType'>): number {
  if (plan.billingCycle === 'annual' || plan.planType === 'annual') return 2;
  if (plan.planType === 'monthly_annual' || plan.planType === 'annual_installment') return 1;
  return 0;
}

/**
 * PIX na renovação, pelo plano ESCOLHIDO:
 *   `mensal`        — plano mensal puro: PIX do mês.
 *   `anual_a_vista` — plano anual à vista: PIX do valor TOTAL do ano, uma vez.
 *   null            — mensal com fidelidade e anual parcelado: só cartão.
 * Espelho de `renewalPixMode` do backend (subscriptions/utils/renewal-mode.ts)
 * — mudou lá, mudar aqui. Aqui é só o seletor; quem recusa é o POST
 * /payments/pix (`pix_indisponivel`).
 */
export type RenewalPixMode = 'mensal' | 'anual_a_vista';

export function pixNaRenovacao(
  plan: Pick<RenewalPlanOption, 'billingCycle' | 'planType'>,
): RenewalPixMode | null {
  if (plan.billingCycle === 'annual' || plan.planType === 'annual') return 'anual_a_vista';
  return plan.planType === 'monthly' ? 'mensal' : null;
}

/**
 * Renovação nunca desce (23/09/2026): o próprio plano ou um que não baixe nem
 * nível nem compromisso. Espelho de `isRenewalPlanAllowed` do backend — mudou
 * lá, mudar aqui. Aqui é só o seletor; quem recusa é o POST /payments
 * (`plano_inferior`).
 */
export function isRenewalPlanAllowed(
  escolhido: RenewalPlanOption,
  atual: RenewalPlanOption,
): boolean {
  if (escolhido.id === atual.id) return true;
  const nivelAtual = planTier(atual.name);
  const nivelNovo = planTier(escolhido.name);
  if (nivelAtual === null || nivelNovo === null) return false;
  return nivelNovo >= nivelAtual && planCommitment(escolhido) >= planCommitment(atual);
}

/**
 * Opções que o cliente pode escolher: o catálogo visível e ativo que passa na
 * régua "renovação nunca desce", com o plano ATUAL sempre presente — plano
 * descontinuado (fora do catálogo) não pode desaparecer do seletor, senão o
 * cliente perderia a opção de simplesmente continuar no que já tinha. O
 * backend aplica a mesma exceção.
 *
 * `planoOfertado` (oferta fechada da automação): o seletor tem só ele.
 */
export function buildRenewalPlanOptions(
  catalogo: unknown,
  planoAtual: RenewalPlanOption,
  planoOfertado: RenewalPlanOption | null = null,
): RenewalPlanOption[] {
  if (planoOfertado) return [planoOfertado];

  const lista: RawPlanInput[] = Array.isArray(catalogo) ? catalogo : [];
  const doCatalogo = lista
    .filter(isVisibleAndActive)
    .map(toOption)
    .filter((plan): plan is RenewalPlanOption => !!plan && plan.discountedPrice > 0)
    .filter((plan) => isRenewalPlanAllowed(plan, planoAtual));

  const opcoes = doCatalogo.some((plan) => plan.id === planoAtual.id)
    ? doCatalogo
    : [planoAtual, ...doCatalogo];

  const vistos = new Set<string>();
  return opcoes.filter((plan) => {
    if (vistos.has(plan.id)) return false;
    vistos.add(plan.id);
    return true;
  });
}

export function toRenewalPlanOption(
  plan: RawPlanInput | null | undefined,
): RenewalPlanOption | null {
  return toOption(plan);
}

/**
 * Mensalidade equivalente de tabela em CENTAVOS — espelho de
 * `isUpgradePlanSuperior` do backend (renewal-eligibility.ts): anual divide
 * por 12, comparação em centavos pra decimal do pg não empatar errado.
 */
export function mensalidadeEquivalenteCents(plan: RenewalPlanOption): number {
  return Math.round((custoAnualizado(plan) / 12) * 100);
}

/**
 * Opções do UPGRADE: planos do catálogo com mensalidade equivalente maior que
 * a do plano atual — EMPATE entra quando a família é diferente (espelho de
 * `isUpgradePlanSuperior` do backend: Descoberta mensal → Explorador anual tem
 * a MESMA mensalidade equivalente e é upgrade de tier legítimo). O atual fica
 * FORA do seletor (mesmo plano não é upgrade; o backend recusa com
 * `mesmo_plano`). O filtro aqui é só UX: quem manda é a régua server-side
 * (`plano_nao_superior`). Ordenado do mais barato pro mais caro — o primeiro
 * é o pré-selecionado.
 *
 * `planoOfertado` (oferta fechada do painel, campanha mensal → anual): o
 * seletor tem só ele, mesmo que a régua de "superior" o recuse (Aventureiro
 * mensal → Explorador anual). Quem valida a oferta é o backend
 * (`recusaDaOfertaDeUpgrade`), na emissão, na hidratação e no pagamento.
 */
export function buildUpgradePlanOptions(
  catalogo: unknown,
  planoAtual: RenewalPlanOption,
  planoOfertado: RenewalPlanOption | null = null,
): RenewalPlanOption[] {
  if (planoOfertado) return [planoOfertado];
  const lista: RawPlanInput[] = Array.isArray(catalogo) ? catalogo : [];
  const atualCents = mensalidadeEquivalenteCents(planoAtual);
  const familiaAtual = normalizePlanFamily(planoAtual.name);

  const vistos = new Set<string>();
  return lista
    .filter(isVisibleAndActive)
    .map(toOption)
    .filter((plan): plan is RenewalPlanOption => !!plan && plan.discountedPrice > 0)
    .filter((plan) => plan.id !== planoAtual.id)
    .filter((plan) => {
      const cents = mensalidadeEquivalenteCents(plan);
      if (cents > atualCents) return true;
      return cents === atualCents && normalizePlanFamily(plan.name) !== familiaAtual;
    })
    .filter((plan) => {
      if (vistos.has(plan.id)) return false;
      vistos.add(plan.id);
      return true;
    })
    .sort((a, b) => mensalidadeEquivalenteCents(a) - mensalidadeEquivalenteCents(b));
}

/** Custo de 12 meses no plano: anual já é o ano fechado, mensal é 12x. */
export function custoAnualizado(plan: RenewalPlanOption): number {
  return plan.billingCycle === 'annual'
    ? plan.discountedPrice
    : plan.discountedPrice * 12;
}

const ehMensalPuro = (plan: RenewalPlanOption) =>
  plan.billingCycle === 'monthly' && (plan.planType ?? 'monthly') === 'monthly';

/**
 * Economia de um plano com compromisso anual contra o mensal equivalente
 * (mesma família). Sem plano da família, cai no mensal MAIS BARATO do catálogo
 * — referência conservadora: só sobra economia se ela existir de verdade.
 * Devolve null quando não há o que anunciar.
 */
export function calcularEconomiaAnual(
  plan: RenewalPlanOption,
  todos: RenewalPlanOption[],
): number | null {
  if (ehMensalPuro(plan)) return null;

  const anualizado = custoAnualizado(plan);
  if (!(anualizado > 0)) return null;

  const referencias = todos.filter(
    (candidato) => ehMensalPuro(candidato) && candidato.discountedPrice > 0,
  );
  if (referencias.length === 0) return null;

  const familia = normalizePlanFamily(plan.name);
  const daFamilia = referencias.filter(
    (candidato) => normalizePlanFamily(candidato.name) === familia,
  );
  const base = (daFamilia.length > 0 ? daFamilia : referencias).reduce(
    (menor, candidato) =>
      candidato.discountedPrice < menor.discountedPrice ? candidato : menor,
  );

  const economia = base.discountedPrice * 12 - anualizado;
  // Diferença de centavos é arredondamento de tabela, não economia.
  return economia >= 1 ? economia : null;
}
